import { fromFetch } from 'rxjs/fetch';
import { from, of } from 'rxjs';
import { map, switchMap, mergeMap, catchError } from 'rxjs/operators';
import { ofType } from 'redux-observable';
import { GeneralError } from '@feathersjs/errors';

import { smilesToMol, molfileToMol, molToSmiles } from '../conversion';
// import config from '@f/config.json';
import { createAction } from '../actions';
import {
  CONVERT_SMILES_TO_MOL_START,
  CONVERT_SMILES_TO_MOL_FULFILL,
  CONVERT_SMILES_TO_MOL_REJECT,
  CONVERT_MOLFILE_TO_MOL_START,
  CONVERT_MOLFILE_TO_MOL_FULFILL,
  CONVERT_MOLFILE_TO_MOL_REJECT,
  CONVERT_MOL_TO_SMILES_START,
  CONVERT_MOL_TO_SMILES_FULFILL,
  CONVERT_MOL_TO_SMILES_REJECT,
  GET_PUBCHEM_MOL_START,
  GET_PUBCHEM_MOL_FULFILL,
  GET_PUBCHEM_MOL_REJECT,
} from './molecule-util.actions';
import { parsePubchemResponse } from './utils/parsePubchemResponse';

// ===================================

export function convertSmilesToMolEpic(action$: any, state$: any) {
  return action$.pipe(
    ofType(CONVERT_SMILES_TO_MOL_START),
    mergeMap((action: any) => {
      const { smiles } = action.payload;
      const { molsBySmiles } = state$.value.moleculeUtil;

      if (molsBySmiles[smiles]) {
        return of(
          createAction(
            CONVERT_SMILES_TO_MOL_FULFILL,
            {
              result: molsBySmiles[smiles],
            },
            action
          )
        );
      }
      const resultPromise = smilesToMol(smiles);

      return from(resultPromise).pipe(
        map(result => createAction(CONVERT_SMILES_TO_MOL_FULFILL, { result }, action)),
        catchError(async error => createAction(CONVERT_SMILES_TO_MOL_REJECT, error))
      );
    })
  );
}

export function convertMolfileToMolEpic(action$: any, state$: any) {
  return action$.pipe(
    ofType(CONVERT_MOLFILE_TO_MOL_START),
    mergeMap((action: any) => {
      const { molfile } = action.payload;
      const { molsByMolfiles } = state$.value.moleculeUtil;

      if (molsByMolfiles[molfile]) {
        return of(
          createAction(
            CONVERT_MOLFILE_TO_MOL_FULFILL,
            {
              result: molsByMolfiles[molfile],
            },
            action
          )
        );
      }

      const resultPromise = molfileToMol(molfile);

      return from(resultPromise).pipe(
        map(result => createAction(CONVERT_MOLFILE_TO_MOL_FULFILL, { result }, action)),
        catchError(async error => createAction(CONVERT_MOLFILE_TO_MOL_REJECT, error))
      );
    })
  );
}

export function convertMolsToSmilesEpic(action$: any) {
  return action$.pipe(
    ofType(CONVERT_MOL_TO_SMILES_START),
    mergeMap((action: any) => {
      const { mols } = action.payload;

      const resultPromise = Promise.all(mols.map((mol: any) => molToSmiles(mol)));

      return from(resultPromise).pipe(
        map(result => createAction(CONVERT_MOL_TO_SMILES_FULFILL, { result }, action)),
        catchError(async error => createAction(CONVERT_MOL_TO_SMILES_REJECT, error))
      );
    })
  );
}

export function getMolByNameEpic(action$: any, state$: any) {
  return action$.pipe(
    ofType(GET_PUBCHEM_MOL_START),
    mergeMap((action: any) => {
      const { nameOrCas } = action.payload;
      const { molsByName } = state$.value.moleculeUtil;

      if (molsByName[nameOrCas]) {
        return of(
          createAction(GET_PUBCHEM_MOL_FULFILL, {
            result: molsByName[nameOrCas],
            params: { nameOrCas },
          })
        );
      }

      const fetchCompoundFromName = new Request(
        `https://pubchem.ncbi.nlm.nih.gov/rest/pug/compound/name/${encodeURIComponent(nameOrCas)}/sdf`,
        {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        }
      );

      return fromFetch(fetchCompoundFromName).pipe(
        switchMap(response => {
          if (response.ok) {
            return response.text();
          }
          throw new Error(nameOrCas);
        }),
        map(result => {
          if (result) {
            return parsePubchemResponse(result) as Record<string, string[]>;
          }
          throw new Error(nameOrCas);
        }),
        switchMap(dict => {
          const [smiles] = dict.PUBCHEM_SMILES;
          return smilesToMol(smiles);
        }),
        map(result =>
          createAction(GET_PUBCHEM_MOL_FULFILL, {
            result,
            params: { nameOrCas },
          })
        ),
        catchError(async baseError => {
          // if (isMolviewError(baseError)) {
          //   return createAction(GET_PUBCHEM_MOL_REJECT, baseError);
          // }

          let error;
          switch (baseError.response.status) {
            case 404:
              error = new Error(nameOrCas);
              break;
            default:
              error = new GeneralError(
                "Something went wrong on our side. We'll do our best to fix it as soon as possible.",
                {
                  type: 'getMolByNameEpic',
                  nameOrCas,
                }
              );
          }

          return createAction(GET_PUBCHEM_MOL_REJECT, error);
        })
      );
    })
  );
}
